'use client';

import * as React from 'react';
import { OTPInput, OTPInputContext } from 'input-otp';
import { cn } from '@src/v3/lib/utils';

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      'flex items-center gap-2 has-[:disabled]:opacity-50',
      containerClassName
    )}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
));
InputOTP.displayName = 'InputOTP';

const InputOTPGroup = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center', className)} {...props} />
));
InputOTPGroup.displayName = 'InputOTPGroup';

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex items-center justify-center',
        isActive && 'z-10',
        className
      )}
      {...props}
    >
      <span className="flex items-center justify-center leading-none">
        {char}
      </span>
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-6 sm:h-8 w-px bg-gray-1000 animate-caret-blink" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = 'InputOTPSlot';

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <path
        d="M8.78158 8.78158C9.00619 8.55697 9.00619 8.19303 8.78158 7.96842L7.03158 6.21842C6.80697 5.99381 6.44303 5.99381 6.21842 6.21842C5.99381 6.44303 5.99381 6.80697 6.21842 7.03158L7.96842 8.78158C8.19303 9.00619 8.55697 9.00619 8.78158 8.78158Z"
        fill="currentColor"
      />
      <path
        d="M3.21842 3.21842C2.99381 3.44303 2.99381 3.80697 3.21842 4.03158L4.96842 5.78158C5.19303 6.00619 5.55697 6.00619 5.78158 5.78158C6.00619 5.55697 6.00619 5.19303 5.78158 4.96842L4.03158 3.21842C3.80697 2.99381 3.44303 2.99381 3.21842 3.21842Z"
        fill="currentColor"
      />
      <path
        d="M8.78158 3.21842C9.00619 3.44303 9.00619 3.80697 8.78158 4.03158L7.03158 5.78158C6.80697 6.00619 6.44303 6.00619 6.21842 5.78158C5.99381 5.55697 5.99381 5.19303 6.21842 4.96842L7.96842 3.21842C8.19303 2.99381 8.55697 2.99381 8.78158 3.21842Z"
        fill="currentColor"
      />
      <path
        d="M3.21842 8.78158C2.99381 8.55697 2.99381 8.19303 3.21842 7.96842L4.96842 6.21842C5.19303 5.99381 5.55697 5.99381 5.78158 6.21842C6.00619 6.44303 6.00619 6.80697 5.78158 7.03158L3.96842 8.78158C3.74381 9.00619 3.37987 9.00619 3.21842 8.78158Z"
        fill="currentColor"
      />
    </svg>
  </div>
));
InputOTPSeparator.displayName = 'InputOTPSeparator';

export {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
  OTPInputContext,
};
